import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IMusicProductPhysicalSettlementInfoMutation } from './types/mutation';
import {
  IMusicProductPhysicalSettlementInfoResponse,
  ISettlementInfoTime,
} from './types/query';

export const usePostMusicProductPhysicalSettlementInfo = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductPhysicalSettlementInfo'
  );
  return useMutation<
    IMusicProductPhysicalSettlementInfoResponse,
    DefaultError,
    IMusicProductPhysicalSettlementInfoMutation
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('music-product-physical.contracts.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MusicProductPhysical/SettlementInfo',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductPhysicalSettlementInfo',
        notFoundPath: '/music-product-physical',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('music-product-physical.contracts.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetMusicProductPhysicalContractFuture = (
  PhysicalMusicProductId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<
    ISettlementInfoTime<IMusicProductPhysicalSettlementInfoResponse>,
    DefaultError
  >({
    queryKey: ['music-product-physical-contract-future', PhysicalMusicProductId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductPhysical/Contract/Future`,
        params: { PhysicalMusicProductId },
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductPhysicalContractFuture - ${PhysicalMusicProductId}`,
      });
    },
  });
};

export const useGetMusicProductPhysicalContractHistory = (
  PhysicalMusicProductId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<
    ISettlementInfoTime<IMusicProductPhysicalSettlementInfoResponse>,
    DefaultError
  >({
    queryKey: ['music-product-physical-contract-history', PhysicalMusicProductId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductPhysical/Contract/History`,
        params: { PhysicalMusicProductId },
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductPhysicalContractHistory - ${PhysicalMusicProductId}`,
      });
    },
  });
};
